import Api from './api';
import { resolveViewRoute } from './publicViewApi';

/**
 * Roteia um endpoint de dados de widget.
 *
 * Os widgets são renderizados tanto no builder (usuário autenticado) quanto na visualização
 * pública, pelos mesmos componentes. Este helper decide, por request, se vai para a rota
 * autenticada ou para o equivalente em /public/view/reportbuilder/* com o ViewToken.
 */
const postWidgetData = (authenticatedPath, publicPath, payload, config) => {
  const { client, url } = resolveViewRoute(authenticatedPath, publicPath);
  return client.post(url, payload, config);
};

/**
 * Serviço para operações de projetos
 * Integração com ProjectController do backend
 */
class ReportBuilderService {
  static async getMetricList(erp) {
    return await Api.get('/reportbuilder/getMetricList', { params: { erp } });
  }

  static async getDREData(
    erp,
    externalId,
    dimension,
    metric,
    dataini,
    datafim,
    compiledFilters,
    regimeType = 'emissao',
    dreGroupBy = undefined,
    config = undefined,
  ) {
    const payload = {
      erp: erp,
      externalId: externalId,
      dimension: dimension,
      metric: metric,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
      DREDateFilter: regimeType
    };

    // Envia DREGroupBy apenas quando definido (retrocompatível)
    if (dreGroupBy !== undefined && dreGroupBy !== null) {
      payload.DREGroupBy = dreGroupBy;
    }

    return await postWidgetData(
      '/reportbuilder/getDREData',
      '/public/view/reportbuilder/dre-data',
      payload,
      config,
    );
  }

  static async getDFCData(
    erp,
    externalId,
    dataini,
    datafim,
    compiledFilters,
    regimeType = 'pagamento',
    config = undefined,
  ) {
    return await postWidgetData('/reportbuilder/getDFCData', '/public/view/reportbuilder/dfc-data', {
      erp: erp,
      externalId: externalId,
      dimension: 0,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
      DFCDateFilter: regimeType
    }, config);
  }

  static async getABCData(erp, externalId, dimension, groups, cutA, cutB, dataini, datafim, compiledFilters) {
    return await postWidgetData('/reportbuilder/getABCData', '/public/view/reportbuilder/abc-data', {
      erp: erp,
      externalId: externalId,
      dimension: dimension,
      groups: groups,
      cutA: cutA,
      cutB: cutB,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters
    });
  }

  static async getVisualData(
    erp,
    externalId,
    dimension,
    metric,
    dataini,
    datafim,
    compiledFilters,
    regime,
    widgetType = undefined,
    config = undefined,
  ) {
    return await postWidgetData('/reportbuilder/getVisualData', '/public/view/reportbuilder/visual-data', {
      erp: erp,
      externalId: externalId,
      dimension: dimension,
      metric: metric,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
      regime: regime,
      ...(widgetType ? { widgetType } : {})
    }, config);
  }

  static async getVisualDetailData(payload, config = undefined) {
    return await postWidgetData(
      '/ReportBuilder/GetVisualDetailData',
      '/public/view/reportbuilder/visual-detail-data',
      payload,
      config,
    );
  }

  static async getTableData(
    erp,
    externalId,
    metric,
    dimensionsArray,
    dataini,
    datafim,
    compiledFilters,
    optionsOrConfig = undefined,
    config = undefined,
  ) {
    let includeDates = false;
    let regime = undefined;
    let effectiveConfig = config;

    const looksLikeAxiosConfig =
      optionsOrConfig &&
      typeof optionsOrConfig === "object" &&
      !Array.isArray(optionsOrConfig) &&
      (Object.prototype.hasOwnProperty.call(optionsOrConfig, "signal") ||
        Object.prototype.hasOwnProperty.call(optionsOrConfig, "headers") ||
        Object.prototype.hasOwnProperty.call(optionsOrConfig, "params") ||
        Object.prototype.hasOwnProperty.call(optionsOrConfig, "timeout") ||
        Object.prototype.hasOwnProperty.call(optionsOrConfig, "baseURL"));

    if (typeof optionsOrConfig === "string") {
      regime = optionsOrConfig;
    } else if (
      optionsOrConfig &&
      typeof optionsOrConfig === "object" &&
      !Array.isArray(optionsOrConfig) &&
      (Object.prototype.hasOwnProperty.call(optionsOrConfig, "includeDates") ||
        Object.prototype.hasOwnProperty.call(optionsOrConfig, "regime"))
    ) {
      includeDates = Boolean(optionsOrConfig.includeDates);
      regime = optionsOrConfig.regime;
    } else if (looksLikeAxiosConfig && effectiveConfig === undefined) {
      effectiveConfig = optionsOrConfig;
    }

    return await postWidgetData('/ReportBuilder/GetTableData', '/public/view/reportbuilder/table-data', {
      erp: erp,
      externalId: externalId,
      metric: metric,
      dimensions: dimensionsArray,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
      ...(includeDates ? { includeDates: true } : {}),
      ...(regime ? { regime } : {}),
    }, effectiveConfig);
  }

  static async getTitulosStatusData(payload, config = undefined) {
    return await postWidgetData(
      '/ReportBuilder/GetTitulosStatusData',
      '/public/view/reportbuilder/titulos-status-data',
      payload,
      config,
    );
  }

  static async getNFData(payload, config = undefined) {
    return await Api.post('/ReportBuilder/GetNFData', payload, config);
  }

  static async getFluxoCaixaData(
    erp,
    externalId,
    dataini,
    datafim,
    compiledFilters,
    regime = 'pagamento',
    usaSaldo = false,
    config = undefined,
  ) {
    return await postWidgetData('/reportbuilder/getFluxoCaixaData', '/public/view/reportbuilder/fluxo-caixa-data', {
      erp: erp,
      externalId: externalId,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
      regime: regime,
      usaSaldo: usaSaldo
    }, config);
  }

  static async getSaldosData(
    erp,
    externalId,
    compiledFilters,
    daysForward = 30,
    includeSolvency = false,
    dateBase = "vencimento",
    status = []
  ) {
    return await postWidgetData('/reportbuilder/getSaldosData', '/public/view/reportbuilder/saldos-data', {
      erp: erp,
      externalId: externalId,
      dimensionFilters: compiledFilters,
      daysForward: daysForward,
      includeSolvency: includeSolvency,
      dateBase: dateBase,
      status: status
    });
  }

  static async getMatrixData(erp, externalId, metric, dimensionsArray, columnDimensionsArray, dataini, datafim, compiledFilters) {
    return await Api.post('/reportbuilder/getMatrixData', {
      erp: erp,
      externalId: externalId,
      metric: metric,
      dimensions: dimensionsArray,
      columnDimensions: columnDimensionsArray,
      period: {
        start: dataini,
        end: datafim
      },
      dimensionFilters: compiledFilters,
    });
  }

  static async saveTemplate(templateData) {
    return await Api.post('/reportbuilder/saveTemplate', templateData);
  }

  static async getTemplates() {
    return await Api.get('/reportbuilder/getTemplates');
  }

  static async loadTemplate(templateId) {
    return await Api.get(`/reportbuilder/getTemplate/${templateId}`);
  }

  //==============================
  // Seção de filtro de dimensão
  //==============================
   /**
   * Retorna o universo (todos os valores possíveis) para uma dimensão.
   * Ex.: [{ id:'cat_10', name:'Bebidas' }, ...]
   */
   static async getDimensionValues(erpId, externalId, dimension, payload = undefined) {
    return await postWidgetData(
      '/reportbuilder/getDimensionValues',
      '/public/view/reportbuilder/dimension-values',
      {
        erp: erpId,
        externalId,
        dimension,
        ...(payload && typeof payload === "object" ? payload : {}),
      },
    );
  }
  //==============================
  // Fim da seção de filtro de dimensão
  //==============================
}

export default ReportBuilderService; 
